import { useFormikContext } from 'formik'
import { FC, useCallback, useContext, useMemo, useState } from 'react'
import {
  FormBlock,
  FormikSelectInput,
  FormikSwitch,
  Icon,
  IconButton,
  RoundedButton,
  TooltipWrapper,
} from '../../../shared'
import { getV3ColorCssVariable } from '../../../../design-system/colors_v3'
import { IdentityCheckStatus, TrustedSource } from '../../../../model/Patient'
import { PatientFormFields } from '../PatientForm.model'
import { IdentityStatusBlockProps } from './IdentityStatusBlock.model'
import { IdentityStatusCondition } from './IdentityStatusCondition'
import classNames from 'classnames'
import {
  formatDateWithDaytime,
  formatShortFrStringToApiFormat,
} from '../../../../misc/date.utilities'
import { InformationAlert } from '../../../user'
import { InsiPatientFirstnameChangeValidationModal } from './InsiPatientFirstnameChangeValidationModal'
import {
  compareOIDValues,
  deserializeInsiPatient,
  deserializeInsiPayload,
  formatInsiPatientNames,
  formatNamesList,
  formatPatientNames,
} from './IdentityStatusBlock.utils'
import { useDisableInsiFields } from '../useDisableInsiFields.hook'
import { patientToInsiRestPatientPayload } from '../../../../misc/insi.utilities'
import { ConnectedUserContext } from '../../../../misc/auth.utilities'
import { isDoctor } from '../../../../misc/user.utilities'
import { useCreateTask } from '../../../../hooks/queries/task'
import { useUserEnabledFeature } from '../../../../hooks/utils/user'
import { FeaturesType } from '@follow/cdk'
import { useInsiIdentityCheck } from '../../../../hooks/queries/insi'
import type { InsiIdentityCheckPayload, InsiPatient } from '../../../../model/Insi'
import styles from './IdentityStatusBlock.module.scss'
import { compareObjects } from '../../../../misc/objects.utilities'
import type { ComparisonResultItem } from '../../../../model/ComparisonResult.type'
import { InsiReviewAndValidateModal } from './InsiReviewAndValidateModal'
import { TaskCategory } from '../../../../model/Task'
import { useGetTrustedSources } from '../../../../hooks/queries/admin/trustedSource'
import { useGetIdentityState } from '../../../../hooks/utils/patient/useGetIdentityState.hook'
import { InsCheckTypeEnums } from '../../../../features/ins/enums/insCheckType.enums'
import { InsEventTypeEnums } from '../../../../features/ins/enums/insEventType.enums'
import {
  InsCheckTypeValue,
  InsStatusCheckTypeValue,
} from '../../../../features/ins/types/ins.types'
import { INS_LOG_FIELD_MAPPINGS } from '../../../../features/ins/constants/insFieldsToCompare.constant'
import { InsStatusCheckTypeEnums } from '../../../../features/ins/enums/insStatusCheckType.enums'
import { useCreateResourceHistory } from '../../../../hooks/queries/resourceHistory'
import { ResourceHistoryMutationPayload } from '../../../../data/resourceHistory/mapper'
import InsUtilities from '../../../../features/ins/utilities/Ins.utilities'

const getIdentityCheckLabel = (status: IdentityCheckStatus, checkedAt: Date | null) => {
  const formatedDate = checkedAt ? formatDateWithDaytime(checkedAt) : ''
  switch (status) {
    case 'UNCHECKED':
      return 'Non réalisée'
    case 'VALIDATED':
      return `Succès le ${formatedDate}`
    case 'FAILED':
      return `Échec le ${formatedDate}`
  }
}

export const IdentityStatusBlock: FC<IdentityStatusBlockProps> = ({
  impersonatePermissions,
  patient,
}) => {
  const { values, errors, setFieldValue, setValues } = useFormikContext<PatientFormFields>()
  const [firstnameModalOpen, setFirstnameModalOpen] = useState(false)
  const [showCompareINSIModal, setShowCompareINSI] = useState(false)
  const [compareINSIResult, setCompareINSIResult] =
    useState<ComparisonResultItem<InsiPatient & PatientFormFields, string>[]>()

  const { mutate: createResourceHistory } = useCreateResourceHistory()

  const [pendingPatientUpdate, setPendingPatientUpdate] = useState<PatientFormFields>()
  const { currentUser } = useContext(ConnectedUserContext)

  const insiService = useUserEnabledFeature(FeaturesType.insiValidation)

  const { mutate: createTask } = useCreateTask()
  const { mutate: identityCheck } = useInsiIdentityCheck()

  const {
    query: { isLoading, data: sources },
  } = useGetTrustedSources()

  const disableInsiFields = useDisableInsiFields()

  const identityState = useGetIdentityState(values.identityStatus)

  const allSources = useMemo(() => {
    if (isLoading || (!sources?.defaultSources && !sources?.customSources)) {
      return []
    }

    return [...(sources.defaultSources || []), ...(sources.customSources || [])]
  }, [sources, isLoading])

  const shownDocumentOptions = useMemo(() => {
    const baseOption = { value: '', label: 'Aucun' }

    if (isLoading || !allSources.length) {
      return [baseOption]
    }
    const sourceOptions = allSources.map((source) => ({
      value: source.value,
      label: source.configuration?.isEnabled
        ? `${source.value} (Haut degré de confiance)`
        : source.value,
    }))
    const documentOptions = [baseOption, ...sourceOptions]

    const selectedValue = values.identityStatus.trustedSource
    if (selectedValue) {
      const isInArray = documentOptions.find((option) => option.value === selectedValue)
      if (!isInArray) {
        documentOptions.push({ label: selectedValue, value: selectedValue })
      }
    }
    return documentOptions
  }, [allSources, isLoading, values.identityStatus.trustedSource])

  const shownDocumentValue = useMemo(() => {
    const selectedValue = values.identityStatus.trustedSource
    return (
      shownDocumentOptions.find((option) => option.value === selectedValue) ??
      shownDocumentOptions[0]
    )
  }, [values.identityStatus.trustedSource, shownDocumentOptions])

  const handleResetINSIState = useCallback(
    (value: boolean, patient: PatientFormFields) => {
      if (value) {
        patient.identityStatus.insiStatus.checked = 'UNCHECKED'
        patient.identityStatus.insiStatus.checkedAt = null
        setFieldValue('inseeNumber', '')
        setFieldValue('assigningAuthority.oid', null)
      }
    },
    [setFieldValue],
  )

  const applyPendingPatientUpdate = useCallback(
    (pendingUpdate: PatientFormFields, overrideFirstname?: boolean, firstName?: string) => {
      setValues(
        {
          ...pendingUpdate,
          birthFirstName: overrideFirstname && firstName ? firstName : values.birthFirstName,
          otherFirstNames: formatNamesList(pendingUpdate.otherFirstNames?.split(' ')).join(' '),
        },
        true,
      )
      setPendingPatientUpdate(undefined)
      setFirstnameModalOpen(false)
    },
    [setValues, values],
  )

  const handleIdentityCheck = useCallback(
    (payload: InsiIdentityCheckPayload | null, type?: InsCheckTypeValue) => {
      identityCheck(
        { patient: payload, type },
        {
          onSuccess: (insiResponse) => {
            const insiPatient = insiResponse?.patient || null
            const type = insiResponse?.type

            if (insiPatient === null && insiResponse?.codeCR) {
              if (type) {
                const insLog = InsUtilities.generateNewInsPatientResourceHistory({
                  eventType: InsEventTypeEnums.INS_CHECK,
                  checkType: type,
                  patientId: `${patient?.id}`,
                  status: insiResponse.codeCR as InsStatusCheckTypeValue,
                  extraData: {},
                })

                createResourceHistory(insLog as unknown as ResourceHistoryMutationPayload)
                createTask({
                  title: patient?.id
                    ? "Échec de la validation de l'identité via l'INSi"
                    : `Échec de la validation de l'identité via l'INSi pour le patient ${values.birthFirstName} ${values.birthLastName}`,
                  autoCreated: true,
                  category: TaskCategory.INFORMATION,
                  patientId: patient?.id ?? undefined,
                  isNotify: true,
                })
              }
            } else {
              const formattedValues = formatPatientNames(values)
              const formattedInsiPatient = formatInsiPatientNames(insiPatient as InsiPatient)
              if (!formattedInsiPatient) return

              const deserializedPatient = deserializeInsiPatient(
                formattedInsiPatient,
                formattedValues,
              )
              const deserializedInsiPayload = deserializeInsiPayload(formattedInsiPatient)
              let insiCompareResult = compareObjects(
                deserializedInsiPayload as InsiPatient & {
                  otherFirstNames?: string
                },
                formattedValues,
              )

              if (insiCompareResult) {
                insiCompareResult = compareOIDValues(insiCompareResult)
                setCompareINSIResult(insiCompareResult)
                setPendingPatientUpdate(deserializedPatient)
                setShowCompareINSI(true)

                if (type) {
                  const insLog = InsUtilities.generateNewInsPatientResourceHistory({
                    eventType: InsEventTypeEnums.INS_CHECK,
                    checkType: type,
                    status: insiResponse?.patient?.codeCR as InsStatusCheckTypeValue,
                    patientId: `${patient?.id}`,
                    extraData: InsUtilities.extractComparisonDifferences(
                      insiCompareResult,
                      INS_LOG_FIELD_MAPPINGS,
                    ),
                  })

                  createResourceHistory(insLog as unknown as ResourceHistoryMutationPayload)
                }
                return
              } else {
                if (
                  formattedInsiPatient.birthFirstName !== formattedValues.birthFirstName &&
                  !disableInsiFields
                ) {
                  setPendingPatientUpdate(deserializedPatient)
                  setFirstnameModalOpen(true)
                } else {
                  applyPendingPatientUpdate(deserializedPatient)
                }
              }
            }
          },
          onError: (insiErrorResponse) => {
            setFieldValue('identityStatus.insiStatus', {
              checked: 'FAILED',
              checkedAt: new Date(),
            })
            createTask({
              title: patient?.id
                ? "Échec de la validation de l'identité via l'INSi"
                : `Échec de la validation de l'identité via l'INSi pour le patient ${values.birthFirstName} ${values.birthLastName}`,
              autoCreated: true,
              category: TaskCategory.INFORMATION,
              patientId: patient?.id ?? undefined,
              isNotify: true,
            })
            setFieldValue('inseeNumber', undefined)
            setFieldValue('assigningAuthority.oid', null)

            const typeResponse = insiErrorResponse?.type

            const resourceHistory = InsUtilities.generateNewInsPatientResourceHistory({
              eventType: InsEventTypeEnums.INS_CHECK,
              checkType: typeResponse || type || InsCheckTypeEnums.PATIENT_IDENTITY,
              status:
                (insiErrorResponse?.codeCR as InsStatusCheckTypeValue) ||
                InsStatusCheckTypeEnums.KO,
              patientId: `${patient?.id}`,
              extraData: {
                errorMessage: (insiErrorResponse?.error as unknown as { error: string })?.error,
              },
            })
            createResourceHistory(resourceHistory)
          },
        },
      )
    },
    [
      createResourceHistory,
      patient?.id,
      values,
      disableInsiFields,
      applyPendingPatientUpdate,
      createTask,
      identityCheck,
      setFieldValue,
    ],
  )

  const handleCheckPatientIdentity = useCallback(() => {
    const serializedPatient = patientToInsiRestPatientPayload({
      ...values,
      birthDate: formatShortFrStringToApiFormat(values.birthDate),
    })

    if (serializedPatient) {
      handleIdentityCheck(serializedPatient, InsCheckTypeEnums.PATIENT_IDENTITY)
    }
  }, [handleIdentityCheck, values])

  const handleCheckVitale = useCallback(() => {
    handleIdentityCheck(null, InsCheckTypeEnums.VITAL_CARD)
  }, [handleIdentityCheck])

  const disableAction =
    !!errors.birthFirstName ||
    !!errors.birthLastName ||
    !!errors.sex ||
    !!errors.birthDate ||
    values.identityStatus.suspicious ||
    values.identityStatus.fictitious ||
    (impersonatePermissions !== null && !impersonatePermissions?.permissions.insiServiceAccess)

  const checkStatus = values.identityStatus.insiStatus.checked

  const isInsiInAppActive = insiService === 'insiValidationInApp'

  return (
    <FormBlock
      label={
        <span className={styles.identityStatusBlockTitle}>
          <span>Statut de l'identité :</span>
          <Icon
            icon="bullet"
            color={getV3ColorCssVariable('status', identityState.color)}
            size="nano"
          />
          <span>{identityState.label}</span>
        </span>
      }
      icon="fileChecked"
    >
      <div className={styles.identityStatusBlockOptionsContainer}>
        {values.identityStatus.trustedSource !== TrustedSource.TRUSTED_CONTACT ? (
          <>
            <FormikSelectInput<string | null>
              label="Justificatif présenté par le patient"
              fieldName="identityStatus.trustedSource"
              options={shownDocumentOptions}
              value={shownDocumentValue}
              onChange={() => {
                setFieldValue('identityStatus.showTrustedSource', false)
              }}
              testId="patient-identity_status-trusted_source"
            />
            <FormikSwitch
              fieldName="identityStatus.showTrustedSource"
              name="Valider l’identité"
              borderBottom
              disabled={
                isDoctor(currentUser) &&
                !allSources.find(
                  (item) =>
                    item?.value === shownDocumentValue.value && item?.configuration?.isEnabled,
                )
              }
              testId="switch-patient-identity_status-valid"
            />
          </>
        ) : (
          <InformationAlert>
            L'identité du patient est certifée car elle provient d'un contact de confiance
          </InformationAlert>
        )}
        <TooltipWrapper content="À activer si vous estimez que l'identité du patient est douteuse">
          <FormikSwitch
            fieldName="identityStatus.suspicious"
            name={`Identité douteuse`}
            borderBottom
            onChange={(value) => handleResetINSIState(value, values)}
            testId="switch-patient-identity_status-is_suspicious"
          />
        </TooltipWrapper>
        <TooltipWrapper content="À activer si vous estimez que l'identité du patient est frauduleuse">
          <FormikSwitch
            fieldName="identityStatus.fictitious"
            name={`Identité fictive`}
            borderBottom
            onChange={(value) => handleResetINSIState(value, values)}
            testId="switch-patient-identity_status-is_fictitious"
          />
        </TooltipWrapper>
      </div>
      {insiService && (
        <div className={styles.identityStatusBlockInsiValidationContainer}>
          <div className={styles.identityStatusBlockInsiCheck}>
            <div className={styles.identityStatusBlockInsiCheckStatusContainer}>
              <span className={styles.identityStatusBlockInsiCheckTitle}>Validation INSi</span>
              <span
                className={classNames(styles.identityStatusBlockInsiCheckStatus, {
                  [styles.unchecked]: checkStatus === 'UNCHECKED',
                  [styles.validated]: checkStatus === 'VALIDATED',
                  [styles.failed]: checkStatus === 'FAILED',
                })}
              >
                {getIdentityCheckLabel(checkStatus, values.identityStatus.insiStatus.checkedAt)}
              </span>
            </div>
            <TooltipWrapper
              display={
                impersonatePermissions !== null &&
                !impersonatePermissions?.permissions.insiServiceAccess
              }
              content="Le médecin ne vous permet pas d'accéder au téléservice INSI en tant qu'assistant.e"
            >
              <div className={styles.identityStatusBlockInsiCheckButton}>
                <RoundedButton
                  disabled={disableAction}
                  label="Vérifier"
                  onClick={handleCheckPatientIdentity}
                  appearance={isInsiInAppActive ? 'left' : 'standalone'}
                  testId="button-patient_identity-check"
                />
                {isInsiInAppActive && (
                  <IconButton
                    disabled={disableAction}
                    icon="caret"
                    rotate={180}
                    appearance="right"
                    theme="primary"
                    showOptionsCaret={false}
                    options={[
                      {
                        label: "Vérification de l'identité patient",
                        onClick: handleCheckPatientIdentity,
                      },
                      {
                        label: 'Vérification par carte vitale',
                        onClick: handleCheckVitale,
                      },
                    ]}
                    testId="button-patient_details-insi_check-options-toggle"
                  />
                )}
              </div>
            </TooltipWrapper>
          </div>
          <div className={styles.identityStatusBlockInsiCheckInformationsContainer}>
            <span className={styles.identityStatusBlockInsiCheckInformationsQuestion}>
              Voulez-vous faire vérifier les informations du patient auprès de l'INSi ?
            </span>
            <span className={styles.identityStatusBlockInsiCheckInformationsListTitle}>
              Champs nécessaires pour la vérification :
            </span>
            <div className={styles.identityStatusBlockInsiCheckInformationsList}>
              <IdentityStatusCondition label="Nom de naissance" valid={!errors.birthLastName} />
              <IdentityStatusCondition label="Prénom de naissance" valid={!errors.birthFirstName} />
              <IdentityStatusCondition label="Sexe" valid={!errors.sex} />
              <IdentityStatusCondition label="Date de naissance" valid={!errors.birthDate} />
              <IdentityStatusCondition
                label="Identité non douteuse et non fictive"
                valid={!values.identityStatus.suspicious && !values.identityStatus.fictitious}
              />
            </div>
          </div>
        </div>
      )}
      <InsiPatientFirstnameChangeValidationModal
        display={firstnameModalOpen}
        userInputFirstname={values.birthFirstName}
        insiResponseFirstname={pendingPatientUpdate?.birthFirstName ?? ''}
        onSubmit={(firstName) =>
          pendingPatientUpdate && applyPendingPatientUpdate(pendingPatientUpdate, true, firstName)
        }
        onClose={() =>
          pendingPatientUpdate && applyPendingPatientUpdate(pendingPatientUpdate, false)
        }
      />
      {compareINSIResult && (
        <InsiReviewAndValidateModal
          display={showCompareINSIModal}
          comparisonResults={compareINSIResult}
          onSubmit={(firstName) => {
            pendingPatientUpdate && applyPendingPatientUpdate(pendingPatientUpdate, true, firstName)

            setCompareINSIResult(undefined)
            setShowCompareINSI(false)
            return
          }}
          onClose={() => {
            setPendingPatientUpdate(undefined)
            setCompareINSIResult(undefined)
            setShowCompareINSI(false)
            return
          }}
        />
      )}
    </FormBlock>
  )
}
